import { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual } from './utils';

type SearchParamProps = Record<string, string | number | undefined>;

export const useRoutingAlt = (filterKeys: string[]) => {
  const [aborterRef, setAbortRef] = useState(new AbortController());
  const [searchParams, setSearchParams] = useState(new URLSearchParams(window.location.search));
  const [filters, setFilters] = useState<SearchParamProps>({});

  const getFiltersParams = (queryParams: string, keys: string[]) => {
    const newSearchParams = new URLSearchParams(queryParams);
    const newQueryParams: { [key: string]: string | number } = {};

    newSearchParams.forEach((value, key) => {
      if (keys.includes(key)) {
        newQueryParams[key] = Number.isInteger(Number(value)) ? Number(value) : value;
      }
    });
    return newQueryParams;
  };

  useEffect(() => {
    const newFilters = getFiltersParams(window.location.search, filterKeys);

    if (!shallowEqual(filters, newFilters)) {
      setFilters(newFilters);
    }
  }, [window.location.search, searchParams]);

  const handleFirstLoad = useCallback(() => {
    if (aborterRef) aborterRef.abort();
    setAbortRef(new AbortController());
  }, [aborterRef]);

  const getSearchParam = useCallback(
    (param: string) => {
      return searchParams.get(param) || undefined;
    },
    [searchParams]
  );

  const setSearchParamsFn = useCallback(
    (params: SearchParamProps) => {
      const newSearchParams = new URLSearchParams(searchParams.toString());
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined) {
          newSearchParams.set(key, params[key]!.toString());
        } else {
          newSearchParams.delete(key);
        }
      });
      setSearchParams(newSearchParams);
      window.history.pushState(null, '', `?${newSearchParams.toString()}`);
    },
    [searchParams]
  );

  const clearAndSetSearchParams = useCallback(
    (params: SearchParamProps, paramsToKeep: string[] = []) => {
      const newSearchParams = new URLSearchParams();

      // First, add the parameters we want to keep
      paramsToKeep.forEach((key) => {
        const value = searchParams.get(key);
        if (value !== null) {
          newSearchParams.set(key, value);
        }
      });

      // Then, add or overwrite with the new parameters
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined) {
          newSearchParams.set(key, params[key]!.toString());
        }
      });

      setSearchParams(newSearchParams);
      window.history.pushState(null, '', `?${newSearchParams.toString()}`);
    },
    [searchParams]
  );

  const cleanSearchParams = useCallback(
    (params?: string[]) => {
      const newSearchParams = new URLSearchParams(window.location.search);
      const currentKeys: string[] = [];
      newSearchParams.forEach((_, k) => currentKeys.push(k));
      (params || currentKeys).forEach((key) => {
        newSearchParams.delete(key);
      });
      setSearchParams(newSearchParams);
      window.history.pushState(null, '', `?${newSearchParams.toString()}`);
    },
    []
  );

  const searchParamsUpdated = useMemo(() => ({ updated: true }), [searchParams]);

  return {
    aborterRef,
    cleanSearchParams,
    getSearchParam,
    handleFirstLoad,
    setSearchParams: setSearchParamsFn,
    searchParamsUpdated,
    clearAndSetSearchParams,
    filters,
  };
};